import { ScrollingModule as ExperimentalScrollingModule } from '@angular/cdk-experimental/scrolling'
import { CdkVirtualScrollViewport, ScrollingModule } from '@angular/cdk/scrolling'
import { CommonModule, DatePipe, formatCurrency, getCurrencySymbol } from '@angular/common'
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, HostListener, inject, Input, OnDestroy, OnInit, ViewChild } from '@angular/core'
import { UntypedFormBuilder } from '@angular/forms'
import { MatDialog } from '@angular/material/dialog'
import { MatIconModule } from '@angular/material/icon'
import { MatTable, MatTableDataSource, MatTableModule } from '@angular/material/table'
import { ActivatedRoute } from '@angular/router'
import { PersonaFieldTypes } from '@app/modules/personas/enums/persona-fields-types.enum'
import { PersonaTypes } from '@app/modules/personas/enums/persona-types.enum'
import { PersonaField } from '@app/modules/personas/interfaces/persona-field.interface'
import { Persona } from '@app/modules/personas/interfaces/persona.interface'
import { PersonaService } from '@app/modules/personas/services/persona.service'
import { DialogComponent } from '@app/modules/shared/components/dialog/dialog.component'
import { ToastrService } from '@app/services/toastr.service'
import { LogoComponent } from '@app/v2/shared/components/logo/logo.component'
import { AlertService } from '@app/v2/shared/services/alert.service'
import { AuthService } from '@auth/services/auth.service'
import { WorkspaceRoleService } from '@auth/services/workspace-role.service'
import { EditRegistrationModalComponent } from '@database/components/edit-registration-modal/edit-registration-modal.component'
import { ApprovalStatus } from '@database/models/registration-status.enum'
import { Registration } from '@database/models/registration.model'
import { RegistrationsDataSourceCollection } from '@database/models/registrations.datasource-collection'
import { DatabaseService } from '@database/services/database.service'
import { environment } from '@environments/environment'
import { ServerRepresentedEvent } from '@events/models/event.model'
import { EventService } from '@events/services/event.service'
import { NbButtonModule, NbCardModule, NbCheckboxModule, NbDialogRef, NbDialogService, NbIconModule, NbSpinnerModule, NbToggleModule } from '@nebular/theme'
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader'
import { NgxTippyModule } from 'ngx-tippy-wrapper'
import { forkJoin, of, Subject } from 'rxjs'
import { debounceTime, first, switchMap } from 'rxjs/operators'
import { RegistrationsDataSource } from '../../models/registrations.datasource'
import { DataHeaderComponent } from '../data-header/data-header.component'
import { DaysFilterComponent } from '../days-filter/days-filter.component'
import { PersonaSwitcherComponent } from '../persona-switcher/persona-switcher.component'
import { ApprovalStatusesComponent } from './components/approval-statuses/approval-statuses.component'

import { DestroyRef } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { EventService as EventServiceV2 } from '@app/v2/event/services/event.service'
import { DropdownComponent } from '@app/v2/shared/components/dropdown/dropdown.component'
import { ComponentWithDataSetter } from '@app/v2/shared/components/dropdown/types/dropdown-option.type'
import { Alert } from '@app/v2/shared/enums/alert.enum'
import { SavedFilter } from '@database/models/saved-filter.interface'
import { BulkProgressService } from '@database/services/bulk-progress.service'
import { BulkProgressToastComponent } from '../bulk-progress-toast/bulk-progress-toast.component'
import { ColumnFilter, ColumnFilterDropdownComponent } from '../column-filter-dropdown/column-filter-dropdown.component'
import { ResendConfirmationFlowComponent } from '../resend-confirmation-flow/resend-confirmation-flow.component'
import { CommentModalComponent } from './components/comment-modal/comment-modal.component'
import { EmptyDataTableComponent } from './components/empty-data-table/empty-data-table.component'
import { FlagDropdownComponent } from './components/flag-dropdown/flag-dropdown.component'
import { PrintHistoryComponent } from './components/print-history/print-history.component'

type PersonaSwitcherItem = {
  title: string
  count: number
  active: boolean
  childCount?: number
  originalIndex?: number
}

@Component({
  selector: 'ngx-events-data-table',
  templateUrl: './data-table.component.html',
  styleUrls: ['./data-table.component.scss'],
  standalone: true,
  providers: [DatePipe, EventServiceV2, EventService],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    DataHeaderComponent,
    PersonaSwitcherComponent,
    MatTableModule,
    MatIconModule,
    NbSpinnerModule,
    NbCheckboxModule,
    NbIconModule,
    NgxTippyModule,
    NbButtonModule,
    NbToggleModule,
    NbCardModule,
    ScrollingModule,
    ExperimentalScrollingModule,
    LogoComponent,
    ApprovalStatusesComponent,
    BulkProgressToastComponent,
    DropdownComponent,
    EmptyDataTableComponent,
    NgxSkeletonLoaderModule,
    FlagDropdownComponent
  ]
})
export class DataTableComponent implements OnInit, OnDestroy {
  @Input() eventId: number
  @Input() selectedPersonaSlug?: string | null
  @ViewChild(MatTable) table: MatTable<any>
  @ViewChild(CdkVirtualScrollViewport) viewport: CdkVirtualScrollViewport
  @ViewChild('columnFilterDropdown') columnFilterDropdown?: DropdownComponent
  expandedElement: Registration | null
  expandedRowId: number | string | null = null

  // Performance optimization: Cache for valueToShow results
  private _valueToShowCache = new Map<string, string>()
  private _scrollAnimationFrame: number | null = null
  private readonly _destroyRef = inject(DestroyRef)

  /** Checks whether an element is expanded. */
  isExpanded(element: any) {
    if (!element) return false
    // Prefer id-based match to avoid reference issues
    return this.expandedRowId != null ? element.id === this.expandedRowId : this.expandedElement === element
  }

  /** Checks if a row is selected - considers both individual selection and allSelected flag */
  isRowSelected(registrant: Registration): boolean {
    if (!registrant) return false
    const ds = this.getCurrentRegistrationsDataSource()
    // If allSelected is true, row is selected unless explicitly deselected
    if (ds?.allSelected) {
      return registrant.selected !== false
    }
    // Otherwise, only selected if explicitly selected
    return registrant.selected === true
  }

  /** Checks if a child row is selected - considers both individual selection and childAllSelected flag */
  isChildRowSelected(registrant: Registration): boolean {
    if (!registrant) return false
    const ds = this.getCurrentRegistrationsDataSource()
    // If childAllSelected is true, row is selected unless explicitly deselected
    if (ds?.childAllSelected) {
      return registrant.selected !== false
    }
    // Otherwise, only selected if explicitly selected
    return registrant.selected === true
  }
  /** MatRowDef predicate: determines if the detail row should render */
  isExpandedRow = (_index: number, row: any) => {
    return row && (this.expandedRowId != null ? row.id === this.expandedRowId : this.isExpanded(row))
  }
  /** To determine if the event has started */
  isEventStarted = false
  /** Image viewer dialog ref */
  imageViewerRef: NbDialogRef<any>
  /** Image source to be used in the dialog */
  imageSource = ''
  /** Reminder price for the current workspace */
  reminderPrice: { amount: number; currency: string }
  /** Times to rotate image */
  rotation = 3
  /** Available rotation angles from the API */
  rotationAngles: Array<number> = [-90, 180, 90, 0]
  /** To show loader when rotating image */
  isRotatingImage = false

  private _isParentRowClicked = false

  /** On destroy implementation */
  ngOnDestroy() {
    this.registrationsDataSourceCollection?.destroy()

    // Clean up performance optimizations
    if (this._scrollAnimationFrame) {
      cancelAnimationFrame(this._scrollAnimationFrame)
    }
    this._valueToShowCache.clear()
  }

  /**
   * Collection of data sources used to represent event registrations
   */
  public registrationsDataSourceCollection: RegistrationsDataSourceCollection = null

  /**
   * Filters for the data sources
   */
  filters: { status: boolean; searchTerm: boolean; refunded?: boolean } = { status: false, searchTerm: false }
  /** determine if there is a search term value */
  hasFilters = false

  /** The current event being displayed */
  public event: ServerRepresentedEvent

  /** True while event/forms are loading before registrationsDataSourceCollection is set. */
  public isDatabaseInitializing = false

  /** Array to track which days are selected in the filter */
  public selectedDays = []

  /** Status filter selection - 'yes' for attended, 'no' for not attended */
  public selectedStatus: 'yes' | 'no' = 'yes'

  /** Array of column names to display in the table */
  public displayedColumns: string[] = []

  /** The current child form being displayed */
  public currentChildForm: any

  /** Array of column definitions for the child form */
  public columnsDefinition: any[] = []

  /** Data source for the table */
  dataSource = new MatTableDataSource<any>()
  /** Whether any child rows are selected */
  childSelected = false
  /** Whether any parent rows are selected */
  parentSelected = false
  /** Whether all child rows are selected */
  allChildsSelected = false
  /** Whether child selection mode is active (hides parent checkboxes) */
  childSelectionActive = false
  /** Whether parent selection mode is active (hides child checkboxes) */
  parentSelectionActive = false
  /** Last page number for pagination */
  lastPage
  /** Current page number for pagination */
  currentPage = 1
  /** Currently selected registrant */
  currentRegistrant
  /** Array containing child registration data */
  childrenData: any[] = []
  /** Loading states */
  isPersonaSwitching = false
  isChildTableLoading = false
  private targetPersonaName = ''

  /**
   * Auto refresh interval id
   */
  private _autoRefreshInterval
  /**
   * Whether we should pool on the data
   */
  private _autoRefresh = false
  /**
   * Last search term the user used
   */
  private _lastSearchTerm = ''

  /**
   * Getter for last search term (used in template)
   */
  get lastSearchTerm(): string {
    return this._lastSearchTerm
  }

  /**
   * Last pixel we scrolled to - handles infinite loading
   */
  lastScrollHeight = 0

  showtable = true
  private _currentDataSource: RegistrationsDataSource
  private _limitChange$ = new Subject<{ registrant: Registration; value: number }>()
  private _pendingChanges = new Map<string | number, { increment: number; decrement: number }>()

  // Column filter and sort state
  /** Currently active filter column (dropdown open) */
  activeFilterColumn: string | null = null
  /** Active column filters - field name to filter config */
  columnFilters: Map<string, { values: string[]; inverted: boolean }> = new Map()
  /** Current sort field (null = default created_at) */
  sortField: string | null = null
  /** Current sort direction */
  sortDirection: 'asc' | 'desc' = 'desc'
  /** Currently hovered column (for showing filter icon) */
  hoveredColumn: string | null = null

  /**
   * Creates the component and injects it's dependencies
   */
  constructor(
    private datePipe: DatePipe,
    private route: ActivatedRoute,
    private service: DatabaseService,
    private eventService: EventService,
    private authService: AuthService,
    private dialogService: NbDialogService,
    private cdr: ChangeDetectorRef,
    private fb: UntypedFormBuilder,
    private toastService: ToastrService,
    private dialog: MatDialog,
    private _alertService: AlertService,
    private bulkProgressService: BulkProgressService,
    private personaService: PersonaService,
    private workspaceRoleService: WorkspaceRoleService
  ) {}

  /**
   * Opens channel + cost flow for a single registrant (Email / SMS / WhatsApp via bulk API when needed).
   */
  openResendConfirmationForRegistrant(registrant: Registration) {
    this.dialogService.open(ResendConfirmationFlowComponent, {
      context: {
        mode: 'single',
        attendeeCount: 1,
        eventSlug: this.event.slug,
        registrationId: registrant.id,
        registration: registrant
      },
      autoFocus: false,
      closeOnBackdropClick: true,
      closeOnEsc: true
    })
  }

  openPrintHistory(registrant: Registration) {
    this.dialogService.open(PrintHistoryComponent, {
      context: {
        registrant
      },
      autoFocus: false
    })
  }

  getPrintTooltip(registrant: Registration): string | null {
    if (!registrant?.hasPrintHistory()) {
      return null
    }
    const person = registrant.latestPrintPerson || 'Unknown'
    const viaFace = registrant.latestPrintViaFacialRecognition ? ' (facial recognition)' : ''
    const personLabel = `${person}${viaFace}`
    const rawDate = registrant.latestPrintAt
    const parsed = new Date(rawDate)
    if (!rawDate || Number.isNaN(parsed.getTime())) {
      return `Latest print: ${personLabel}`
    }
    const formatted = this.datePipe.transform(parsed, 'MMM d, y, h:mm a')
    return `Latest print: ${personLabel} - ${formatted}`
  }

  /**
   * Requests change detection
   */
  public update() {
    this.cdr.detectChanges()
  }

  /**
   * Initializes the component
   */
  ngOnInit(): void {
    this.service.dataAlerts$.pipe(takeUntilDestroyed(this._destroyRef)).subscribe((alert) => {
      this._alertService.alert(alert.type, alert.message)
    })
    // Fetch reminder price
    const planId = this.authService.currentWorkspace.plan_id
    this.authService
      .getPlansWithPrices()
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((prices) => {
        const price = prices.find((o) => o.id === planId)
        this.reminderPrice = {
          amount: price?.reminderPrice?.unit_amount,
          currency: price?.reminderPrice?.currency
        }
        this.cdr.detectChanges()
      })
    const eventId = Number(this.eventId || this.route.snapshot.params.id)
    this._initializeDataTable(eventId)

    this._subscribeToLimitChange()
    this._subscribeToBulkOperationCompletions()
  }

  private _initializeDataTable(eventId: number): void {
    this.isDatabaseInitializing = true
    this.cdr.markForCheck()

    forkJoin([this.eventService.getEvent(eventId), this.eventService.getEventForms(eventId)])
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: async ([event, allForms]) => {
          try {
            if ((allForms?.length ?? 0) === 0) {
              return
            }

            this.initForms(allForms)
            this.event = event

            this.checkSeatsEnabled(this.event)
            event['event_timings'] = (event['event_timings'] || []).filter((date) => {
              const startDateTime = new Date(date?.start_datetime || event.public_start_date)
              const twoHoursBeforeTime = startDateTime.setHours(startDateTime.getHours() - 2)
              return twoHoursBeforeTime < Date.now()
            })
            this.event['event_timings'].sort((a, b) => {
              return (new Date(a.start_datetime) as any) - (new Date(b.start_datetime) as any)
            })
            const counts = {}
            const result = await this.service.getExportableCountsForEvent(event.slug).toPromise()
            allForms?.forEach((f) => {
              if (!result.forms[f.slug]) {
                counts[f.slug] = 0
                return
              }
              counts[f.slug] = Object.keys(result.forms[f.slug]).reduce(
                (p, c) => p + Object.keys(result.forms[f.slug][c]).reduce((p1, c1) => p1 + result.forms[f.slug][c][c1], 0),
                0
              )
            })
            // Create data source collection with all forms (including MEMBER forms)
            const userContext = {
              isAdmin: this.authService.isAdmin,
              approvedBy: this.authService.approvedByPlanningPayload,
              displayName: this.authService.userDisplayName,
              email: this.authService.userEmail
            }
            this.registrationsDataSourceCollection = new RegistrationsDataSourceCollection(
              this.service,
              this.authService.currentWorkspace.slug,
              event,
              allForms,
              counts,
              this.cdr,
              this.workspaceRoleService,
              {},
              userContext
            )

            if (this.selectedPersonaSlug) {
              const selectedIndex = this._resolveSelectedPersonaIndex(allForms, this.selectedPersonaSlug)
              if (selectedIndex >= 0 && this.registrationsDataSourceCollection.registrationsDataSources[selectedIndex]) {
                this.registrationsDataSourceCollection.selectedIndex = selectedIndex
              }
            }

            // Initialize currentDataSource after registrationsDataSourceCollection is created
            this._currentDataSource = this.registrationsDataSourceCollection.registrationsDataSources[this.registrationsDataSourceCollection.selectedIndex]?.registrationsDataSource

            this.update()

            if (this.registrationsDataSourceCollection.registrationsDataSources.length === 0) {
              this.cdr.detectChanges()
            } else {
              this.registrationsDataSourceCollection.changed$.pipe(takeUntilDestroyed(this._destroyRef)).subscribe(() => {
                // Sync child table data with datasource after operations (ex: deletion)
                const currentDataSource =
                  this.registrationsDataSourceCollection.registrationsDataSources[this.registrationsDataSourceCollection.selectedIndex]?.registrationsDataSource
                if (currentDataSource && this.expandedElement && currentDataSource.childData) {
                  this.childrenData = currentDataSource.childData
                  this.dataSource = new MatTableDataSource(this.childrenData)

                  // Update child selection state
                  this.childSelected = this.childrenData.some((child) => child.selected)

                  // If no children left, consider collapsing the row
                  if (this.childrenData.length === 0) {
                    this.childSelected = false
                    this.childSelectionActive = false
                  }
                }
                this.update()
                this.cdr.detectChanges()
              })
              const parsedStartDate = Date.parse(this.event.registration_start_date)
              this.isEventStarted = isNaN(parsedStartDate) ? Date.parse(this.event.registration_start_date.slice(0, 10)) <= Date.now() : parsedStartDate <= Date.now()
              this.cdr.detectChanges()

              // Ensure child table columns are initialized for the initially selected persona
              // This mirrors the behavior when switching personas via the switcher
              try {
                const initialIndex = this.registrationsDataSourceCollection.selectedIndex
                if (initialIndex !== undefined) {
                  this.onTabChanged({ tabId: initialIndex })
                }
              } catch {}
            }
          } finally {
            this.isDatabaseInitializing = false
            this.cdr.markForCheck()
          }
        },
        error: () => {
          this.isDatabaseInitializing = false
          this.cdr.markForCheck()
        }
      })
  }

  /** Reload database after creating the first persona from the empty state. */
  public onEmptyStatePersonaCreated(): void {
    const eventId = Number(this.eventId || this.route.snapshot.params.id)
    if (!eventId) return
    this._initializeDataTable(eventId)
  }

  /**
   * Subscribe to bulk operation completions to refresh data and clear selections
   * Refreshes data to ensure UI reflects actual backend state (especially for operations without optimistic updates like 'request revision')
   */
  private _subscribeToBulkOperationCompletions() {
    this.bulkProgressService.operationCompleted$.pipe(takeUntilDestroyed(this._destroyRef)).subscribe(({ batchId, operation, status }) => {
      // Get current datasource
      const currentDataSource = this.getCurrentRegistrationsDataSource()

      if (currentDataSource && currentDataSource.initializeRequest) {
        // Reload data to reflect actual backend state
        currentDataSource.initialize(currentDataSource.initializeRequest, true, false)
      }

      // Clear all selections (business requirement)
      this.clearParentSelections()
      this.clearChildSelections()
      this.parentSelected = false
      this.childSelected = false
      this.parentSelectionActive = false
      this.childSelectionActive = false

      // Reset infinite scroll tracker when data refreshes
      this.lastScrollHeight = 0

      // Force change detection to update UI
      this.cdr.detectChanges()
    })
  }

  /**
   * Subscribes to registration limit changes from the input subject.
   *
   * This method listens for changes to a registrant's registration limit,
   * debounces rapid input, and then calls the API (or falls back to local update)
   * to apply the new limit. On completion, the registrant state is updated and
   * the UI is refreshed. On error, a user-visible alert is shown.
   *
   * The subscription is automatically disposed when the component is destroyed.
   */
  private _subscribeToLimitChange() {
    this._limitChange$
      .pipe(
        debounceTime(1000),
        switchMap(({ registrant, value }) => {
          // Indicate an update is in progress for the UI
          registrant.processing = true
          return this._updateRegistrationLimit(registrant, value)
        }),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe({
        /**
         * Called when the registration limit update completes successfully.
         * Updates the registrant state and UI.
         */
        next: ({ registrant, newLimit }) => {
          if (newLimit !== null) {
            registrant.maximum_registrations = newLimit
          }
          this._pendingChanges.delete(registrant.id)
          registrant.processing = false
          this.cdr.detectChanges()
        },
        /**
         * Handler for registration limit update error.
         * Shows an error alert to the user.
         */
        error: (err) => {
          this._alertService.alert(Alert.ERROR, 'Failed to update registration limit')
        }
      })
  }
  /**
   * Opens a registration edit dialog
   */
  openEditDialog(registrant: Registration) {
    const dialog = this.dialogService.open(EditRegistrationModalComponent, {
      context: {
        editUrl: registrant.editUrl
      }
    })
    const editClose$ = dialog.onClose.pipe(takeUntilDestroyed(this._destroyRef)).subscribe(() => {
      registrant.updateSelf()
      editClose$.unsubscribe()
    })
    this.service.setDialogRef(dialog)
  }

  /**
   * Updates the current registrations data sources with the latest search term
   */
  updateDataSources(term = '') {
    this.filters.searchTerm = term ? true : false
    this.hasFilters = Boolean(this.filters.searchTerm || this.filters.status || this.columnFilters.size > 0)
    this._lastSearchTerm = term
    this.registrationsDataSourceCollection.updateSearchTerm(term)

    // Reset infinite scroll tracker when data changes
    this.lastScrollHeight = 0
  }

  updateToggleSelection(event, index) {
    // Clear all child selections and activate parent selection mode
    this.clearChildSelections()
    this.parentSelectionActive = true
    this.childSelectionActive = false

    const ds = this.registrationsDataSourceCollection.registrationsDataSources[index].registrationsDataSource
    ds.hasFilters = this.hasFilters && event
    ds.toggleSelection()

    // Note: toggleSelection() already handles updating all row selections efficiently
    // No need to duplicate the work here - it updates visible rows immediately
    // and defers the rest asynchronously to prevent UI blocking

    // Recompute selectionExists → shows bulk toolbar
    this.parentSelected = this.registrationsDataSourceCollection.selected.length >= 1
    this.cdr.detectChanges()
    // If virtual scroll delayed row rendering, schedule a microtask to flush
    setTimeout(() => this.cdr.detectChanges(), 0)
  }

  updateToggleSelectionChild(event, index) {
    // Only clear parent selections and activate child mode if children are actually expanded
    if (this.expandedElement) {
      this.clearParentSelections()
      this.childSelectionActive = true
      this.parentSelectionActive = false
    }

    this.registrationsDataSourceCollection.registrationsDataSources[this.registrationsDataSourceCollection.selectedIndex].registrationsDataSource.toggleSelectionChild(event)
    this.childSelected = this.childrenData.some((child) => child.selected)

    // Notify the datasource that child selections have changed
    this._currentDataSource.changed$?.next(true)

    // Force change detection to update the UI
    this.cdr.detectChanges()
  }

  checkSeatsEnabled(event) {
    const eventHasSeats = event.options?.seatsIO && event.options?.seatsIO?.season.key
    this.service.updateSeatingEnabled(eventHasSeats)
  }

  filterStatus(filter: { status; flagged; refunded; attended }) {
    if (filter.attended) {
      this.openDaysFilterDialog(filter)
      return
    }

    this.applyFilter({
      status: filter.status,
      flagged: filter.flagged,
      refunded: filter.refunded
    })

    this.resetChildForm()
  }

  private openDaysFilterDialog(filter: { status; flagged; refunded; attended }) {
    const dialogRef = this.dialog.open(DaysFilterComponent, {
      width: '300px',
      panelClass: 'matdialog',
      data: { filter: filter, event: this.event, selectedDays: this.selectedDays, selectedStatus: this.selectedStatus },
      hasBackdrop: true
    })

    dialogRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (result) => {
          if (!result) return
          const filterObject = this.buildFilterObject(filter, result)
          this.selectedDays = result.days
          this.selectedStatus = result.status
          this.applyFilter(filterObject)
        }
      })
  }

  private buildFilterObject(filter: any, result: any) {
    return {
      status: filter.status,
      flagged: filter.flagged,
      refunded: filter.refunded,
      days: result.days,
      attended: result.status
    }
  }

  private applyFilter(filterObject: any) {
    this.filters.status = Boolean(filterObject.status || filterObject.flagged || filterObject.refunded || filterObject.days?.length || filterObject.attended)

    this.hasFilters = Boolean(
      this.filters.searchTerm || this.filters.status || this.filters.refunded || filterObject.days?.length || filterObject.attended || this.columnFilters.size > 0
    )

    this.registrationsDataSourceCollection.filterStatus(filterObject)

    // Reset infinite scroll tracker when filters change
    this.lastScrollHeight = 0
  }

  /**
   * Whether a selection exists
   */
  get selectionExists() {
    const sources = this.registrationsDataSourceCollection?.registrationsDataSources
    if (!sources?.length) {
      return false
    }

    return (
      this.registrationsDataSourceCollection.selectionExists ||
      sources[this.registrationsDataSourceCollection.selectedIndex]?.registrationsDataSource?.childSelectionStatus !== 'None'
    )
  }

  /**
   * Handles the event when a user selects a different tab
   */
  onTabChanged(event) {
    this.resetChildForm()

    // Reset infinite scroll tracker when switching tabs
    this.lastScrollHeight = 0

    setTimeout(() => {
      this.registrationsDataSourceCollection.clearSelections()
      this.registrationsDataSourceCollection.selectedIndex = event.tabId

      // Update currentDataSource when switching tabs
      this._currentDataSource = this.registrationsDataSourceCollection.registrationsDataSources[this.registrationsDataSourceCollection.selectedIndex].registrationsDataSource

      // Restore filter state from datasource's initializeRequest if filters are present
      this._restoreFilterStateFromDataSource()

      if (
        this.registrationsDataSourceCollection.registrationsDataSources[this.registrationsDataSourceCollection.selectedIndex].registrationsDataSource.form.type ==
        PersonaTypes.GROUP
      ) {
        // Get the current form ID from the selected data source
        const currentFormId = this.getCurrentRegistrationsDataSource().form.id

        let childForm = null

        // Method 1: Using service.forms.value - search through all forms
        if (this.service.forms?.value) {
          childForm = this.service.forms.value.find((form) => form.type == PersonaTypes.MEMBER && form.parent == currentFormId)
        }

        // Method 2: Using registrationsDataSources to find child form
        if (!childForm) {
          this.registrationsDataSourceCollection.registrationsDataSources.forEach((dataSource, index) => {
            const form = dataSource.registrationsDataSource.form
            if (form.type == PersonaTypes.MEMBER && form.parent == currentFormId) {
              childForm = form
            }
          })
        }

        // Method 3: Direct search through all forms if still not found
        if (!childForm && this.service.forms?.value) {
          this.service.forms.value.forEach((form, index) => {
            if (form.type == PersonaTypes.MEMBER && form.parent == currentFormId) {
              childForm = form
            }
          })
        }

        this.currentChildForm = childForm

        if (childForm) {
          this.registrationsDataSourceCollection.registrationsDataSources[this.registrationsDataSourceCollection.selectedIndex].registrationsDataSource.childForm = childForm

          // Set up columns for child table - mimic parent table structure
          this.displayedColumns = []
          this.columnsDefinition = []

          // Fixed sticky columns first (like parent table)
          this.displayedColumns.push('#select', '#flag')

          // Add form elements sorted by weight (like parent table)
          const sortedElements = childForm.form_elements
            .filter(
              (element) =>
                element.form_element_type_id !== PersonaFieldTypes.INFORMATION_MESSAGE &&
                element.form_element_type_id !== PersonaFieldTypes.WARNING_MESSAGE &&
                element.form_element_type_id !== PersonaFieldTypes.LINE_SEPARATOR
            )
            .sort((a, b) => a.weight - b.weight)

          sortedElements.forEach((element) => {
            this.displayedColumns.push(element.name)
            this.columnsDefinition.push({
              header: element.label,
              field: element.name
            })
          })

          // Action columns at the end (like parent table)
          this.displayedColumns.push('#approve', '#approved_by', '#prints')

          // Add email column only if email field exists in the form
          if (childForm.form_elements.find((o) => o.name === 'email')) {
            this.displayedColumns.push('#email')
          }
          // Only add actions column if user can see it
          if (this.workspaceRoleService.canSeeActionsColumn()) {
            this.displayedColumns.push('#actions')
          }
          this.displayedColumns.push('#attend', '#sms', '#whatsapp', '#created_at', '#child-barcode')

          // Add day columns for child table
          this.event.event_timings.forEach((day, index) => {
            this.displayedColumns.push(`#child-day${index + 1}`)
          })
        }
      }
    })
    this.showtable = true
  }

  /**
   * Handles auto refreshing start/ stop
   */
  updateAutoRefresh(event: boolean) {
    this._autoRefresh = event
    if (event) {
      if (!this._autoRefreshInterval) {
        this._autoRefreshInterval = setInterval(() => {
          if (!this._autoRefresh) {
            clearInterval(this._autoRefreshInterval)
          }
          if (!this.registrationsDataSourceCollection.selectionExists) {
            this.updateDataSources(this._lastSearchTerm)
          }
        }, 60000)
      }
    } else {
      if (this._autoRefreshInterval) {
        clearInterval(this._autoRefreshInterval)
      }
    }
  }

  /**
   * Handles infinite loading when the user scrolls
   * Optimized to reduce change detection cycles
   */
  onScroll(e, index) {
    // Use requestAnimationFrame to throttle scroll events
    if (this._scrollAnimationFrame) {
      cancelAnimationFrame(this._scrollAnimationFrame)
    }

    this._scrollAnimationFrame = requestAnimationFrame(() => {
      const tableViewHeight = e.target.offsetHeight
      const tableScrollHeight = e.target.scrollHeight
      const scrollLocation = e.target.scrollTop

      // If the user has scrolled within 200px of the bottom, add more data
      const buffer = 200
      const limit = tableScrollHeight - tableViewHeight - buffer
      if (scrollLocation > limit && this.lastScrollHeight !== tableScrollHeight) {
        this.lastScrollHeight = tableScrollHeight
        this.registrationsDataSourceCollection.registrationsDataSources[index].registrationsDataSource.loadMore()
      }
    })
  }

  /**
   * A trackBy function to facilitate large datasets manipulation
   */
  trackBy(index, item: Registration) {
    return item.id
  }

  attend(registrant) {
    const checkins = this.registrationsDataSourceCollection.registrationsDataSources[this.registrationsDataSourceCollection.selectedIndex].registrationsDataSource.form.checkins

    let isPhysical = false
    if (Array.isArray(checkins)) isPhysical = checkins.some((o) => o?.slug === 'global')
    else if (checkins && typeof checkins === 'object') isPhysical = checkins.slug === 'global'

    registrant.attend(isPhysical ? 'global' : 'global_virtual')
  }

  unattend(registrant) {
    registrant.unattend()
  }

  /**
   * If the field is radio button, then show the value name instead of the slug
   * Optimized with caching to improve performance during scrolling
   */
  valueToShow(form: Persona, column, registrant) {
    let fieldValue = registrant.calculatedFields[column.field]?.value ?? ''

    const cacheKey = `${registrant.id}-${column.field}-${JSON.stringify(fieldValue)}`

    // Check cache first
    if (this._valueToShowCache.has(cacheKey)) {
      return this._valueToShowCache.get(cacheKey)
    }

    // Field keys can be namespaced in datasource (e.g. "stu-email", "stu-radio1"),
    // while form element names remain unnamespaced ("email", "radio1").
    const normalizedFieldName = this.getUnnamespacedFieldName(column?.field)
    let element = form.form_elements.find((o) => o.name === normalizedFieldName) || form.form_elements.find((o) => o.name === column.field)

    let result: string
    // Handle radio button and gender fields
    if (
      element?.form_element_type_id === PersonaFieldTypes.RADIO ||
      element?.form_element_type_id === PersonaFieldTypes.GENDER ||
      element?.form_element_type_id === PersonaFieldTypes.AGE_GROUP
    ) {
      return element?.form_element_values?.find((o) => (o.slug && o.slug.toLowerCase()) === (typeof fieldValue === 'string' && fieldValue.toLowerCase()))?.name || fieldValue || ''
    }
    // Handle date fields with enable_timer option
    else if (element?.form_element_type_id === PersonaFieldTypes.DATE) {
      if (!fieldValue) {
        result = ''
      } else {
        const hasTimer = element?.options?.enable_timer === true
        if (hasTimer) {
          // Show date and time format
          result = this.datePipe.transform(fieldValue, 'MMM dd, yyyy HH:mm') || ''
        } else {
          // Show date only format
          result = this.datePipe.transform(fieldValue, 'MMM dd, yyyy') || ''
        }
      }
    }
    // Handle numeric fields to ensure zero values are displayed
    else if (element?.form_element_type_id === PersonaFieldTypes.NUMERIC)
      result = typeof fieldValue !== 'number' && typeof fieldValue !== 'string' ? '' : (fieldValue as string).toString()
    // Handle city field for member (child) vs regular personas
    else if (element?.form_element_type_id === PersonaFieldTypes.CITY) {
      if (form?.type === PersonaTypes.MEMBER && fieldValue && typeof fieldValue === 'object') result = fieldValue?.city || ''
      else result = fieldValue || ''
    } else if (element?.form_element_type_id === PersonaFieldTypes.CHECKBOX) {
      if (Array.isArray(fieldValue) && fieldValue.length > 0) {
        result = fieldValue
          .map((slug) => {
            const option = element.form_element_values?.find((o) => o.slug && o.slug.toLowerCase() === slug?.toLowerCase())
            return '● ' + (option ? option?.name : slug)
          })
          .filter((o) => o !== null)
          .join('\n')
      } else {
        result = ''
      }
    }
    // Return the actual field value for all other field types
    else result = fieldValue || ''

    // Cache the result (limit cache size to prevent memory leaks)
    if (this._valueToShowCache.size > 1000) {
      this._valueToShowCache.clear()
    }
    this._valueToShowCache.set(cacheKey, result)

    return result
  }

  /**
   * Checks if a field is of type UPLOAD in either parent or child form
   * @param fieldName - The name of the field to check
   * @returns True if the field is an upload type, false otherwise
   */
  public isUploadFieldType(fieldName: string): boolean {
    if (!fieldName) return false

    const parentForm = this.getCurrentRegistrationsDataSource()?.form
    const childForm = this.currentChildForm

    const normalizedFieldName = this.getUnnamespacedFieldName(fieldName)
    const parentElement = parentForm?.form_elements?.find((el: PersonaField) => el.name === normalizedFieldName)
    const childElement = childForm?.form_elements?.find((el: PersonaField) => el.name === normalizedFieldName)

    return parentElement?.form_element_type_id === PersonaFieldTypes.UPLOAD || childElement?.form_element_type_id === PersonaFieldTypes.UPLOAD
  }

  /**
   * Gets the tooltip value for a field
   * @param field - The field to get the tooltip value for (optional)
   * @param registrant - The registrant to get the tooltip value for (optional)
   * @param text - The text to get the tooltip value for (optional)
   * @returns The tooltip value as a string, or null to hide the tooltip
   */
  public getTooltipValue(field?: string, registrant?: Registration, text?: string) {
    if (!field && !registrant) return text?.length >= 15 ? text : null

    // If this field is of type UPLOAD, do not show tooltip
    if (field && this.isUploadFieldType(field)) return null

    const fieldProps = registrant.calculatedFields[field]
    if (!fieldProps?.hidden && Array.isArray(fieldProps?.value)) {
      if (Array.isArray(text?.split?.('\n')) && text.split('\n').some((option) => (option?.length || 0) > 15)) {
        return text
      }
    } else if (!fieldProps?.hidden) {
      if (fieldProps?.value?.length >= 40 && !fieldProps?.hidden) return fieldProps.value ? fieldProps.value.toString() : null
    }
    return null
  }

  /**
   * Field-value tooltip content. Empty values get no tooltip.
   */
  public getFieldValueTooltip(value: unknown): string | null {
    if (value == null) return null
    const text = String(value).trim()
    return text || null
  }

  public readonly fieldValueTippyProps = {
    placement: 'top-start' as const
  }

  /**
   * Tooltip for Approved / Declined By column.
   * Approved: AI verdict HTML, or name if long.
   * Declined: name if long.
   */
  public getApprovedByTooltip(registrant: Registration): string | null {
    if (registrant.entry_status === ApprovalStatus.DECLINED) {
      const declinedByName = registrant.declinedBy?.name
      return declinedByName && declinedByName.length >= 25 ? declinedByName : null
    }

    if (!this.isApproved(registrant)) return null

    const approvedByName = registrant.approvedBy?.name
    if (!approvedByName) return null

    // Check if approved by AI and has verdict
    const aiVerdict = registrant['_source']?.third_parts?.ai_approve_verdict
    if (approvedByName === 'MICEtribe AI' && aiVerdict?.reasoning) {
      // Format the reasoning: split by RULE and DECISION, make DECISION bold
      const reasoning = aiVerdict.reasoning
      const lines = reasoning
        .replace(/RULE\s*(\d+)/gi, '\nRULE $1')
        .replace(/DECISION:/gi, '\n<strong>DECISION:</strong>')
        .trim()
        .split('\n')
        .filter((line: string) => line.trim())
        .map((line: string) => line.trim())
        .join('<br>')

      return `<div style="text-align: left; font-size: 12px; line-height: 1.5;">${lines}</div>`
    }

    // Default tooltip for non-AI approvals
    return approvedByName.length >= 25 ? approvedByName : null
  }

  /**
   * Initialize formArray
   */
  initForms(personas: Persona[]) {
    const formsArray = this.fb.array([])
    personas?.forEach((persona) => {
      const languages = persona.options?.languages
      delete persona.options?.languages
      const optionsGroup = this.fb.group({ ...persona.options })
      optionsGroup.addControl('languages', this.fb.control(languages))
      const formElements = this.fb.array([...persona.form_elements])
      const sub_locations_ids = this.fb.array([...persona.checkins.map((o) => o.id)])
      const promo_code_ids = this.fb.array([...persona.promo_codes.map((o) => o.id)])
      const formGroup = this.fb.group({
        ...persona,
        registration_amount: persona.registration_amount || 0,
        form_elements: formElements,
        sub_locations_ids: sub_locations_ids,
        promo_code_ids: promo_code_ids,
        options: optionsGroup,
        record: {}
      })
      formsArray.push(formGroup)
    })
    this.service.forms = formsArray
  }

  /**
   * Check if field is upload and has value
   */
  isUploadedImage(fieldType, fieldData) {
    const pattern = /^(upload.*|.*photo.*)$/
    if (!pattern.test(fieldType) || !fieldData?.value) return false
    const imageExtensions = ['png', 'jpg', 'jpeg', 'gif', 'svg']
    const pathOnly = String(fieldData.value).split('?')[0].split('#')[0]
    const extension = pathOnly.split('.').pop()
    if (imageExtensions.includes(extension?.toLowerCase())) return 'image'
    return 'file'
  }

  /**
   * Open profile picture modal
   */
  openImage(element, imageSource, title?) {
    this.imageViewerRef = this.dialogService.open(element)
    this.imageSource = imageSource
  }

  /**
   * Close profile picture modal and apply the image rotations
   */
  closeImageModal() {
    this.isRotatingImage = true
    const randomDuration = Math.floor((Math.random() * 10 + 5) * 100)
    const fileName = this.imageSource.match(/.*\/(.+?)\./)[1]
    const element = fileName.split('-')[0]
    const registration = fileName.split('-')[1]
    const formSlug = this.registrationsDataSourceCollection.registrationsDataSources[this.registrationsDataSourceCollection.selectedIndex].slug
    if (this.rotation === 3) {
      this.imageSource = ''
      this.isRotatingImage = false
      setTimeout(() => {
        this.imageViewerRef.close()
      }, randomDuration)
      return
    }
    const rotationAngle = this.rotationAngles[this.rotation]
    setTimeout(() => {
      this.service
        .rotateImage(this.authService.currentWorkspace.slug, this.event.slug, formSlug, element, registration, rotationAngle)
        .pipe(takeUntilDestroyed(this._destroyRef))
        .subscribe(
          (res) => {
            this.imageSource = res['url']
            this.isRotatingImage = false
            this.imageViewerRef.close()
            this.imageSource = ''
          },
          (err) => {
            console.error(err)
            this.isRotatingImage = false
            this.imageViewerRef.close()
          }
        )
      this.imageSource = ''
      this.rotation = 3
    }, randomDuration)
  }
  /**
   * Checks if the current form is a group form
   */
  isGroupForm(): boolean {
    const dataSource = this.registrationsDataSourceCollection.registrationsDataSources[this.registrationsDataSourceCollection.selectedIndex].registrationsDataSource
    return dataSource.form.type === PersonaTypes.GROUP
  }
  /**
   * Rotate the image
   */
  rotateImage() {
    this.isRotatingImage = true
    this.rotation = this.rotation === 3 ? 0 : this.rotation + 1
    const rotationAngle = (this.rotation + 1) * 90
    document.querySelectorAll(`[src="${this.imageSource}"]`).forEach((image) => {
      image['style'].transform = `rotate(${rotationAngle}deg)`
    })
    this.isRotatingImage = false
  }

  /**
   * Returns the notification tooltip message with the notification price
   */
  getReminderPriceMessage() {
    return `It will cost you ${formatCurrency(
      this.reminderPrice?.amount / 100,
      'en',
      getCurrencySymbol(this.reminderPrice?.currency?.toUpperCase(), 'narrow'),
      this.reminderPrice?.currency?.toUpperCase(),
      '1.2-2'
    )} to resend this email.`
  }

  isAttended(registrant, day): { status: string; class: string } {
    const dayDate = this.datePipe.transform(day.start_datetime, 'yyyy-MM-dd')
    const today = this.datePipe.transform(new Date(), 'yyyy-MM-dd')
    const dayDateDiff = new Date(dayDate).getTime() - new Date().getTime()

    // Check if this is today and user hasn't attended yet
    if ((today === dayDate && !this.hasAttendedOnDay(registrant, dayDate)) || dayDateDiff > 0) {
      return { status: 'Expected', class: 'badge-warning text-warning' }
    }

    // Check if user attended on this specific day
    const attended = this.hasAttendedOnDay(registrant, dayDate)
    return {
      status: attended ? 'Attended' : 'Missed',
      class: attended ? 'badge-success' : 'badge-danger'
    }
  }

  /**
   * Checks if a registrant attended on a specific day.
   * Uses both attendanceDays (legacy) and attendance_tags.timings (new) data.
   *
   * @param registrant - The object representing the registrant, containing attendance information.
   * @param dayDate - The date string (in 'yyyy-MM-dd' format) for which attendance should be checked.
   * @returns True if the registrant attended on the given day, false otherwise.
   */
  private hasAttendedOnDay(registrant: Registration, dayDate: string): boolean {
    // First check the new attendance_tags.timings data
    if (registrant.source?.attendance_tags?.timings) {
      const timing = registrant.source.attendance_tags.timings.find((t) => t.day === dayDate)
      if (timing) {
        return timing.attended
      }
    }

    // Fallback to legacy attendanceDays array
    return registrant.attendanceDays?.length && registrant.attendanceDays.includes(dayDate)
  }

  openNewTab(url: string) {
    window.open(url, '_blank')
  }

  /**
   * Gets a form element by name
   */
  private getFormElementByName(persona: Persona, fieldName: string) {
    const normalizedFieldName = this.getUnnamespacedFieldName(fieldName)
    if (persona) return persona.form_elements.find((element: PersonaField) => element.name === normalizedFieldName)
  }

  /**
   * Removes slug namespace from dynamic column fields.
   * Example: stu-email -> email
   */
  private getUnnamespacedFieldName(fieldName: any): string {
    if (typeof fieldName !== 'string') return fieldName
    const firstDashIndex = fieldName.indexOf('-')
    if (firstDashIndex <= 0) return fieldName
    return fieldName.slice(firstDashIndex + 1)
  }

  /**
   * Checks if the form element is of a specific type
   */
  isElementOfType(persona: Persona, fieldName: string, type: string) {
    return this.getFormElementByName(persona, fieldName)?.form_element_type_id === PersonaFieldTypes[type.toUpperCase()]
  }

  /**
   * Checks if the registrant is approved
   */
  isApproved(registrant: Registration) {
    return registrant.entry_status === ApprovalStatus.APPROVED
  }

  /**
   * Opens a new tab with the confirmation link
   */
  openConfirmationLink(registrant: Registration) {
    const editUrl = `${environment.publicFormsUrl}workspaces/${this.authService.currentWorkspace.slug}${registrant.confirmationUrl}`
    try {
      navigator.clipboard.writeText(editUrl)
      this.toastService.success('Link Copied', '')
    } catch (error) {
      this.toastService.error('Error', 'Failed to copy link to clipboard')
    }
    setTimeout(() => {
      this.openNewTab(editUrl)
    }, 500)
  }

  /**
   * Refund the registrant
   */
  refund(registrant: Registration) {
    if (this.isFinishedEvent()) return this.toastService.error('Error', 'This event has ended and cannot be refunded')
    if (!registrant?.payments.length) return this.toastService.error('Error', 'This registrant has not paid yet')
    const lastPayment = registrant.payments[registrant.payments.length - 1]
    if (!lastPayment?.payments) return this.toastService.error('Error', 'This registrant has not paid online and cannot be refunded')
    else if (lastPayment.refund_amount) return this.toastService.error('Error', 'This registrant has already been refunded')

    const currency = lastPayment?.payments.currency
    const amount = lastPayment.amount

    const dialogData = {
      headerText: `Are you sure you want to refund ${amount} ${currency}${registrant.fields?.fullname ? ` to ${registrant.fields?.fullname}` : ''}?`,
      cancelButtonText: 'Cancel',
      confirmButtonText: 'Refund',
      confirmTwoButtonText: 'Refund and Decline',
      confirmButtonClass: 'delete',
      confirmButtonTwoClass: 'delete',
      hideIcon: true
    }
    const _declineModalRef = this.dialog.open(DialogComponent, {
      minWidth: '544px',
      panelClass: 'matdialog',
      data: dialogData
    })

    _declineModalRef
      .afterClosed()
      .pipe(first(), takeUntilDestroyed(this._destroyRef))
      .subscribe((res) => {
        if (res === 'confirm' || res === 'confirmTwo') {
          registrant.refund(amount, res === 'confirm' ? false : true)
        }
      })
  }

  /**
   * Checks if the registrant has been refunded
   */
  refundStatus(registrant) {
    return registrant.refunded
  }

  /**
   * Checks if the event has ended
   */
  isFinishedEvent() {
    return Date.parse(this.event.public_end_date) < Date.now()
  }

  /** Toggles the expanded state of an element. */
  toggle(element: any) {
    this.expandedElement = this.isExpanded(element) ? null : element
  }

  /**
   * Handles the parent table wheel event to avoid interfering with child tables
   */
  onParentTableWheel(event: WheelEvent) {
    // Only block parent scrolling if we're directly over a child table
    const target = event.target as Element
    if (target && target.closest('.child-table-container')) {
      // Don't stop propagation - let the child table handle it naturally
      return
    }
    // Allow normal parent scrolling everywhere else
  }

  /**
   * Handles child table wheel events to prevent parent scroll interference
   */
  onChildTableWheel(event: WheelEvent) {
    const container = event.currentTarget as HTMLElement

    // Check if the child table has scrollable content
    if (container && container.scrollHeight > container.clientHeight) {
      // Has scrollable content - allow natural scrolling and stop propagation to parent
      event.stopPropagation()
      return
    }

    // No scrollable content in child - allow event to bubble to parent for page scrolling
    // Don't prevent default or stop propagation
  }

  /**
   * Handles the child table scroll event
   */
  onChildTableScroll(event: any) {
    const element = event.target
    const tolerance = 5
    const atBottom = element.scrollHeight - element.scrollTop <= element.clientHeight + tolerance

    if (atBottom && this.currentPage < this.lastPage) {
      // Load more data when reaching the bottom
      this.currentPage++
      this.getChildRegistrations(this.currentRegistrant, this.expandedElement, false, true)
    }
  }

  /**
   * Gets child registrations for a given registrant
   */
  getChildRegistrations(registrant: Registration, element: any, toggle = true, loadMore: boolean = false) {
    // Prevent double clicks
    if (toggle && this._isParentRowClicked) return
    // If clicking the already expanded parent row, collapse immediately and exit
    if (toggle && this.expandedRowId === registrant.id) {
      this._isParentRowClicked = true
      this.isChildTableLoading = false
      this.resetChildForm()
      try {
        this.cdr.detectChanges()
        if (this.table) this.table.renderRows()
      } catch {}
      setTimeout(() => (this._isParentRowClicked = false), 100)
      return
    }

    this._isParentRowClicked = true
    // Expand the row immediately
    this.expandedRowId = registrant.id
    this.expandedElement = element

    this.cdr.detectChanges()
    if (this.table) this.table.renderRows()

    // Load child data
    this.loadChildData(registrant, loadMore)
    setTimeout(() => (this._isParentRowClicked = false), 100)
  }

  /**
   * Loads child data for a given registrant
   * @param {Registration} registrant - The registrant to load child data for
   */
  loadChildData(registrant: Registration, loadMore: boolean = false) {
    this.isChildTableLoading = !loadMore && true
    this.cdr.detectChanges()

    // Ensure currentChildForm and displayed columns are prepared
    if (!this.currentChildForm) {
      const currentFormId = this.registrationsDataSourceCollection.registrationsDataSources[this.registrationsDataSourceCollection.selectedIndex].registrationsDataSource.form.id
      let childForm = null as any
      if (this.service.forms?.value) {
        childForm = this.service.forms.value.find((form) => form.type == PersonaTypes.MEMBER && form.parent == currentFormId)
      }
      // Fallback: scan data sources
      if (!childForm) {
        this.registrationsDataSourceCollection.registrationsDataSources.forEach((ds) => {
          const form = ds.registrationsDataSource.form
          if (form.type == PersonaTypes.MEMBER && form.parent == currentFormId) childForm = form
        })
      }
      if (childForm) {
        this.currentChildForm = childForm

        // Also set it in the datasource
        this.registrationsDataSourceCollection.registrationsDataSources[this.registrationsDataSourceCollection.selectedIndex].registrationsDataSource.childForm = childForm

        // Build child columns same as onTabChanged
        this.displayedColumns = ['#select', '#flag']
        this.columnsDefinition = []
        const sortedElements = childForm.form_elements
          .filter((element) => ![PersonaFieldTypes.INFORMATION_MESSAGE, PersonaFieldTypes.WARNING_MESSAGE, PersonaFieldTypes.LINE_SEPARATOR].includes(element.form_element_type_id))
          .sort((a, b) => a.weight - b.weight)
        sortedElements.forEach((element) => {
          this.displayedColumns.push(element.name)
          this.columnsDefinition.push({ header: element.label, field: element.name })
        })
        this.displayedColumns.push('#approve', '#approved_by', '#prints', '#attend')
        if (childForm.form_elements.find((o) => o.name === 'email')) {
          this.displayedColumns.push('#email')
        }
        // Only add actions column if user can see it
        if (this.workspaceRoleService.canSeeActionsColumn()) {
          this.displayedColumns.push('#actions')
        }
        this.displayedColumns.push('#sms', '#whatsapp', '#created_at', '#child-barcode')
        // Add day columns for child table
        this.event.event_timings.forEach((day, index) => {
          this.displayedColumns.push(`#child-day${index + 1}`)
        })
      }
    }

    this.registrationsDataSourceCollection.registrationsDataSources[this.registrationsDataSourceCollection.selectedIndex].registrationsDataSource.currentParentId = registrant.id
    this.eventService
      .getChildRegistrations(this.event.slug, registrant, this.currentPage)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((res: any) => {
        this.currentPage = res.current_page
        this.lastPage = res.last_page

        // Clear previous child data
        this.childrenData = loadMore ? this.childrenData : []

        // Process the children from the API response
        if (res.data && res.data.children && res.data.children.length > 0) {
          res.data.children.forEach((child, index) => {
            // Transform child data to match RegistrationData interface
            const transformedChild = {
              ...child,
              fields: child.fields || child // Use existing fields or the entire child object as fields
            }
            const childRegistration = new Registration(transformedChild, this.childrenData.length + index + 1, this.service, {
              displayName: this.authService.userDisplayName,
              approvedBy: this.authService.approvedByPlanningPayload
            })
            this.childrenData.push(childRegistration)
          })
        }

        // Set up the data source for the child table
        this.dataSource = new MatTableDataSource(this.childrenData)
        this.currentRegistrant = registrant

        // Store child data in the registration data source
        this.registrationsDataSourceCollection.registrationsDataSources[this.registrationsDataSourceCollection.selectedIndex].registrationsDataSource.childData = this.childrenData
        this.registrationsDataSourceCollection.registrationsDataSources[this.registrationsDataSourceCollection.selectedIndex].registrationsDataSource.childDatalength = res.total

        // Update selection modes after data is loaded
        this.updateSelectionModes()

        // Hide loading state after data is loaded - faster for better UX
        setTimeout(() => {
          this.isChildTableLoading = false
          this.cdr.detectChanges()
          // Nudge viewport to stabilize row context and keep detail row rendered
          try {
            if (this.viewport) {
              const currentOffset = this.viewport.measureScrollOffset()
              this.viewport.scrollToOffset(currentOffset + 1)
              this.viewport.scrollToOffset(currentOffset)
            }
          } catch {}
        }, 150)
      })
  }

  /**
   * Handles the child table selection event
   */
  childSelection(event, registrant) {
    // Only clear parent selections and activate child mode if children are actually expanded
    if (this.expandedElement) {
      this.clearParentSelections()
      this.childSelectionActive = true
      this.parentSelectionActive = false
    }

    this.childrenData.find((child) => child.id === registrant.id).selected = event
    const isSelectedChild = this.childrenData.some((child) => child.selected)
    this.childSelected = isSelectedChild

    // Notify the datasource that child selections have changed
    this._currentDataSource = this.registrationsDataSourceCollection.registrationsDataSources[this.registrationsDataSourceCollection.selectedIndex].registrationsDataSource
    if (this._currentDataSource.changed$) {
      this._currentDataSource.changed$.next(true)
    }

    // Force change detection to update the UI
    this.cdr.detectChanges()
  }

  /**
   * Handles the parent table selection event
   */
  parentChecked() {
    // Clear all child selections and activate parent selection mode
    this.clearChildSelections()
    this.parentSelectionActive = true
    this.childSelectionActive = false

    this.parentSelected = this.registrationsDataSourceCollection.selected.length >= 1 ? true : false
    this.cdr.detectChanges()
  }

  /** Handle individual parent row checkbox change to keep datasource selection state in sync */
  onParentRowCheckboxChange(checked: boolean, registrant: Registration) {
    // Keep datasource selection signals flowing for OnPush
    registrant.toggleSelect(checked)

    // Activate parent selection mode when any parent is toggled
    this.clearChildSelections()
    this.parentSelectionActive = true
    this.childSelectionActive = false

    // Recompute toolbar visibility
    this.parentSelected = this.registrationsDataSourceCollection.selected.length >= 1
    // Notify datasource collection of change (propagate changed$)
    try {
      this.registrationsDataSourceCollection.changed$.next(true)
    } catch {}
    this.cdr.detectChanges()
  }

  /**
   * Checks if the parent selection status is all
   */
  isChecked() {
    return this.registrationsDataSourceCollection.registrationsDataSources[this.registrationsDataSourceCollection.selectedIndex].registrationsDataSource.selectionStatus === 'All'
  }

  /**
   * Checks if the parent selection status is indeterminate
   */
  isIndeterminate() {
    return this.registrationsDataSourceCollection.registrationsDataSources[this.registrationsDataSourceCollection.selectedIndex].registrationsDataSource.selectionStatus === 'Some'
  }

  /**
   * Checks if the child selection status is all
   */
  isChildChecked() {
    return (
      this.registrationsDataSourceCollection.registrationsDataSources[this.registrationsDataSourceCollection.selectedIndex].registrationsDataSource.childSelectionStatus === 'All'
    )
  }

  /**
   * Checks if the child selection status is indeterminate
   */
  isChildIndeterminate() {
    return (
      this.registrationsDataSourceCollection.registrationsDataSources[this.registrationsDataSourceCollection.selectedIndex].registrationsDataSource.childSelectionStatus === 'Some'
    )
  }

  /**
   * Changes the status of a child registrant
   */
  changeStatusChild(status, registrant) {
    registrant.changeStatusTo(status)
  }

  /**
   * Marks a child registrant as attended
   */
  attendChild(registrant) {
    try {
      const checkins = this.currentChildForm?.checkins

      let isPhysical = false

      if (Array.isArray(checkins)) isPhysical = checkins.some((o) => o?.slug === 'global')
      else isPhysical = checkins.slug === 'global'

      const spotSlug = isPhysical ? 'global' : 'global_virtual'

      registrant.attend(spotSlug)
    } catch {
      // Fallback to virtual if structure is missing
      registrant.attend('global_virtual')
    }
  }

  /**
   * Marks a child registrant as not attended
   */
  unattendChild(registrant) {
    try {
      registrant.unattend()
    } catch {}
  }

  /**
   * Updates the registration limit for a registrant
   * @param registrant - The registrant to update
   * @param value - The new limit value
   * @returns Observable with registrant and newLimit
   */
  private _updateRegistrationLimit(registrant: Registration, value: number) {
    // Check permission before allowing API call (defense in depth)
    if (!this.canEditRegistrationLimit()) {
      this._alertService.alert(Alert.ERROR, 'You do not have permission to edit registration limits')
      return of({ registrant, newLimit: null })
    }

    // Ensure third_parts field exists
    if (!registrant['third_parts']) registrant['third_parts'] = {}

    const newLimit = Math.max(1, value)
    const current = registrant.maximum_registrations

    // If the new limit is the same as the current one, do nothing
    if (typeof current === 'number' && current === newLimit) {
      return of({ registrant, newLimit: null })
    }

    this.cdr.detectChanges()

    const eventSlug = this.getCurrentRegistrationsDataSource()?.initializeRequest?.event
    const workspaceSlug = this.getCurrentRegistrationsDataSource()?.initializeRequest?.workspace

    // Update limit via API if slugs and ID are available
    if (eventSlug && workspaceSlug && registrant?.id) {
      return this.service.updateRegistrationLimit(eventSlug, workspaceSlug, registrant?.id, newLimit).pipe(
        switchMap(() => of({ registrant, newLimit })),
        takeUntilDestroyed(this._destroyRef)
      )
    } else {
      return of({ registrant, newLimit: null })
    }
  }

  public onLimitInputBlur(registrant: Registration, event: Event) {
    // Check permission before allowing changes
    if (!this.canEditRegistrationLimit()) {
      this._alertService.alert(Alert.ERROR, 'You do not have permission to edit registration limits')
      // Reset input to original value
      const inputElement = event.target as HTMLInputElement
      inputElement.value = registrant.maximum_registrations.toString()
      this.cdr.detectChanges()
      return
    }

    const numValue = Number((event.target as HTMLInputElement).value)
    const correctedValue = numValue < 1 ? 1 : numValue

    // Immediately update the input field if value was less than 1
    if (numValue < 1) {
      const inputElement = event.target as HTMLInputElement
      inputElement.value = '1'
    }

    this._limitChange$.next({ registrant, value: correctedValue })
    this.cdr.detectChanges()
  }

  /**
   * Increments the registration limit for a registrant
   */
  public increment(registrant: Registration) {
    // Check permission before allowing changes
    if (!this.canEditRegistrationLimit()) {
      this._alertService.alert(Alert.ERROR, 'You do not have permission to edit registration limits')
      return
    }

    const pending = this._pendingChanges.get(registrant.id) || { increment: 0, decrement: 0 }
    pending.increment++
    this._pendingChanges.set(registrant.id, pending)
    const current = registrant.maximum_registrations
    this._limitChange$.next({ registrant, value: current + pending.increment - pending.decrement })
    this.cdr.detectChanges()
  }

  /**
   * Decrements the registration limit for a registrant
   */
  public decrement(registrant) {
    // Check permission before allowing changes
    if (!this.canEditRegistrationLimit()) {
      this._alertService.alert(Alert.ERROR, 'You do not have permission to edit registration limits')
      return
    }

    const pending = this._pendingChanges.get(registrant.id) || { increment: 0, decrement: 0 }
    pending.decrement++
    this._pendingChanges.set(registrant.id, pending)
    const current = registrant.maximum_registrations
    this._limitChange$.next({ registrant, value: current + pending.increment - pending.decrement })
    this.cdr.detectChanges()
  }

  public getDisplayedLimit(registrant: Registration): number {
    const pending = this._pendingChanges.get(registrant.id) || { increment: 0, decrement: 0 }
    return registrant.maximum_registrations + pending.increment - pending.decrement <= 1 ? 1 : registrant.maximum_registrations + pending.increment - pending.decrement
  }

  /**
   * Checks if the user can edit registration limits
   * @returns boolean
   */
  public canEditRegistrationLimit(): boolean {
    return this.workspaceRoleService.canEditRegistrationLimit()
  }

  /**
   * Determines whether a row should be shown
   */
  shouldShowRow(): boolean {
    return true
  }

  /**
   * Handles click on calculated field cells to expand/collapse child rows
   */
  onCalculatedFieldClick(event: Event, registrant: any) {
    // Only expand if this is a GROUP form (PersonaTypes.GROUP)
    if (
      this.registrationsDataSourceCollection.registrationsDataSources[this.registrationsDataSourceCollection.selectedIndex].registrationsDataSource.form.type === PersonaTypes.GROUP
    ) {
      this.getChildRegistrations(registrant, registrant)
    }
  }

  /**
   * Clears all parent selections
   */
  clearParentSelections() {
    this.registrationsDataSourceCollection.clearSelections()
    // Also clear individual registrant selected properties
    const currentDataSource = this.getCurrentRegistrationsDataSource()
    if (currentDataSource.data) {
      currentDataSource.data.forEach((registrant) => {
        registrant.selected = false
      })
    }
    this.parentSelected = false
  }

  /**
   * Clears all child selections
   */
  clearChildSelections() {
    if (this.childrenData) {
      this.childrenData.forEach((child) => {
        child.selected = false
      })
    }
    this.childSelected = false

    // Notify the datasource that child selections have changed
    if (this._currentDataSource.changed$) {
      this._currentDataSource.changed$.next(true)
    }
  }

  /**
   * Updates selection modes based on current state
   */
  updateSelectionModes() {
    // If there are expanded children, activate child selection mode and clear parent selections
    if (this.expandedElement) {
      if (!this.childSelectionActive) {
        this.clearParentSelections()
        this.childSelectionActive = true
        this.parentSelectionActive = false
      }
    } else {
      // No expanded children, reset to allow parent selections
      this.childSelectionActive = false
      this.parentSelectionActive = false
    }
  }

  /**
   * Resets the child form data
   */
  resetChildForm() {
    // Clear expanded state first
    this.expandedElement = null
    this.expandedRowId = null
    this.currentChildForm = null
    this.displayedColumns = []
    this.columnsDefinition = []
    this.childSelected = false
    this.childSelectionActive = false
    this.parentSelectionActive = false

    // Reset pagination
    this.currentPage = 1
    this.lastPage = null
  }

  /**
   * Get personas formatted for the persona switcher component
   */
  getPersonasForSwitcher(): Array<PersonaSwitcherItem> {
    if (!this.registrationsDataSourceCollection?.registrationsDataSources) {
      return []
    }
    const personas = this.registrationsDataSourceCollection.registrationsDataSources.map((dataSource, originalIndex) => {
      const form = dataSource.registrationsDataSource.form
      const result: PersonaSwitcherItem = {
        title: dataSource.title,
        count: dataSource.registrationsDataSource.count,
        active: originalIndex === this.registrationsDataSourceCollection.selectedIndex,
        originalIndex: originalIndex
      }
      const childForm = form.subForm
      if (childForm)
        result.childCount = this.registrationsDataSourceCollection.registrationsDataSources.find(
          (ds) => ds.registrationsDataSource.form.id === childForm.id
        )?.registrationsDataSource.count
      return result
    })

    // Sort: GROUP personas before their MEMBER children
    return personas.sort((parent, child) => {
      const parentForm = this.registrationsDataSourceCollection.registrationsDataSources[parent.originalIndex]?.registrationsDataSource.form
      const childForm = this.registrationsDataSourceCollection.registrationsDataSources[child.originalIndex]?.registrationsDataSource.form

      if (parentForm?.type === PersonaTypes.GROUP && childForm?.type === PersonaTypes.MEMBER && childForm?.parent === parentForm?.id) return -1
      return parent.originalIndex - child.originalIndex
    })
  }

  /**
   * Handle persona selection from the switcher
   */
  onPersonaSelected(index: number) {
    if (this.registrationsDataSourceCollection) {
      // Get the personas array to find the correct original index
      const personas = this.getPersonasForSwitcher()
      const selectedPersona = personas[index]

      if (!selectedPersona || selectedPersona.originalIndex === undefined) return

      const originalIndex = selectedPersona.originalIndex

      if (originalIndex === this.registrationsDataSourceCollection.selectedIndex) return
      // Show loading state
      this.isPersonaSwitching = true
      this.targetPersonaName = this.registrationsDataSourceCollection.registrationsDataSources[originalIndex]?.title || 'Unknown'

      // CRITICAL: Complete cleanup before switching personas
      this.expandedElement = null
      this.expandedRowId = null
      this.resetChildForm()

      // Clear all filters and search when switching personas
      this.clearAllFiltersAndSearch()

      // Force change detection to clear any pending renders
      this.cdr.detectChanges()

      // Use a more reliable switching approach
      setTimeout(() => {
        this.onTabChanged({ tabId: originalIndex })

        // Hide loading state after a brief delay to ensure table is rendered
        setTimeout(() => {
          this.isPersonaSwitching = false
          this.cdr.detectChanges()
        }, 150)
      }, 100)
    }
  }

  /**
   * Clear all column filters, sort, and search term when switching personas
   */
  clearAllFiltersAndSearch(): void {
    this.columnFilters.clear()
    this.sortField = null
    this.sortDirection = 'desc'
    this.activeFilterColumn = null
    this.selectedDays = []
    this.selectedStatus = 'yes'

    // Clear UI-level filters only; do NOT clear per-persona initializeRequest.searchTerm
    // so that each persona can restore its own saved filter state when revisited.
    this._lastSearchTerm = ''
    this.filters = { status: false, searchTerm: false, refunded: false }
    this.hasFilters = false
    this.registrationsDataSourceCollection.filterStatus({ status: null, flagged: null, refunded: null, days: null, attended: null }, false)

    // Trigger UI state clear in data-header (chips, etc.)
    this.shouldClearFilterState = true
    setTimeout(() => {
      this.shouldClearFilterState = false
      this.cdr.detectChanges()
    }, 0)
  }

  /**
   * Flag to signal data-header to clear its search term
   */
  shouldClearSearch = false

  /**
   * Value to set search term in data-header (without triggering change event)
   */
  shouldSetSearchTerm: string | null = null

  /**
   * Flag to signal data-header to clear its filter UI state
   */
  shouldClearFilterState = false

  /**
   * Get the current registration data source based on selected index
   */
  getCurrentRegistrationsDataSource() {
    if (!this.registrationsDataSourceCollection?.registrationsDataSources) {
      return null
    }
    return this.registrationsDataSourceCollection.registrationsDataSources[this.registrationsDataSourceCollection.selectedIndex]?.registrationsDataSource
  }

  /**
   * Get the target persona name for loading display
   */
  getTargetPersonaName(): string {
    return this.targetPersonaName
  }

  // ============================================
  // Column Filter and Sort Methods
  // ============================================

  /**
   * Check if a column has an active filter
   */
  hasColumnFilter(field: string): boolean {
    const filter = this.columnFilters.get(field)
    return filter !== undefined && filter.values.length > 0
  }

  /**
   * Get the filter values for a column
   */
  getColumnFilterValues(field: string): string[] {
    const filter = this.columnFilters.get(field)
    return filter?.values || []
  }

  /**
   * Get whether the filter for a column is inverted
   */
  isColumnFilterInverted(field: string): boolean {
    const filter = this.columnFilters.get(field)
    return filter?.inverted || false
  }

  /**
   * Get the form element for a field (for determining filter type)
   */
  getFormElement(field: string): PersonaField | null {
    const dataSource = this.getCurrentRegistrationsDataSource()
    if (!dataSource?.form?.form_elements) {
      return null
    }
    return dataSource.form.form_elements.find((el) => el.name === field) || null
  }

  /** Label for the active filter column */
  activeFilterLabel: string = ''
  /** Synthetic anchor for shared dropdown placement (viewport coords below filter btn) */
  private _filterAnchorEvent: MouseEvent | null = null

  get columnFilterContentSetter(): ComponentWithDataSetter<ColumnFilterDropdownComponent> {
    const field = this.activeFilterColumn
    return {
      component: ColumnFilterDropdownComponent,
      dataSetter: (inst: ColumnFilterDropdownComponent) => {
        if (!field) return
        inst.field = field
        inst.fieldLabel = this.getActiveFilterLabel()
        inst.formElement = this.getFormElement(field)
        inst.selectedValues = this.getColumnFilterValues(field)
        inst.isInverted = this.isColumnFilterInverted(field)
        inst.eventTimings = this.event?.event_timings || []
        inst.onFilterChange = (filter: ColumnFilter) => this.onColumnFilterChange(filter)
        inst.onClose = () => this.columnFilterDropdown?.close()
        inst.initFromData()
      }
    }
  }

  /**
   * Open the column filter dropdown
   * @param event Mouse event
   * @param columnDefinitionOrField Column definition object or field string (for system columns)
   * @param label Optional label for system columns
   */
  openColumnFilter(event: MouseEvent, columnDefinitionOrField: any, label?: string): void {
    event.stopPropagation()
    const field = typeof columnDefinitionOrField === 'string' ? columnDefinitionOrField : columnDefinitionOrField.field
    const fieldLabel = label || (typeof columnDefinitionOrField === 'string' ? columnDefinitionOrField : columnDefinitionOrField.header)

    if (this.activeFilterColumn === field) {
      this.columnFilterDropdown?.closeDropdown()
      this.closeColumnFilter()
      return
    }

    const triggerRect = this.getFilterTriggerRect(event)

    if (this.activeFilterColumn && this.activeFilterColumn !== field) {
      this.columnFilterDropdown?.closeDropdown()
      this.activeFilterColumn = null
      setTimeout(() => {
        this.openFilterForField(field, fieldLabel, triggerRect)
      }, 0)
    } else {
      this.openFilterForField(field, fieldLabel, triggerRect)
    }
  }

  /** Resolve the filter button under the click (not mat-icon / SVG child). */
  private getFilterTriggerRect(event: MouseEvent): DOMRect | null {
    const eventTarget = event.target as HTMLElement | null
    const current = event.currentTarget instanceof HTMLElement ? event.currentTarget : null
    const button = current?.closest?.('button') || eventTarget?.closest?.('button.filter-btn') || eventTarget?.closest?.('button') || eventTarget
    return button?.getBoundingClientRect?.() ?? null
  }

  /** Viewport anchor for filter panel — matches legacy fixed-position math. */
  private createFilterAnchorEvent(rect: DOMRect): MouseEvent {
    const dropdownWidth = 320
    let clientX = rect.left

    if (rect.left + dropdownWidth > window.innerWidth - 20) {
      clientX = Math.max(20, rect.right - dropdownWidth)
    }

    return new MouseEvent('click', {
      clientX,
      clientY: rect.bottom,
      bubbles: true,
      cancelable: true
    })
  }

  /**
   * Helper method to open filter for a specific field
   * @param field Field name
   * @param fieldLabel Field label
   * @param triggerRect Bounding rect of the filter button (viewport coords for position:fixed)
   */
  private openFilterForField(field: string, fieldLabel: string, triggerRect: DOMRect | null): void {
    if (!triggerRect) return

    this._filterAnchorEvent = this.createFilterAnchorEvent(triggerRect)
    this.activeFilterColumn = field
    this.activeFilterLabel = fieldLabel
    this.cdr.detectChanges()

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.columnFilterDropdown?.openDropdown(this._filterAnchorEvent ?? undefined)
      })
    })
  }

  /**
   * Close the column filter dropdown
   */
  closeColumnFilter(): void {
    this.activeFilterColumn = null
    this._filterAnchorEvent = null
    this.cdr.detectChanges()
  }

  /**
   * Handle table scroll - close filter dropdown
   */
  onTableScroll(): void {
    if (this.activeFilterColumn) {
      this.columnFilterDropdown?.closeDropdown()
      this.closeColumnFilter()
    }
  }

  /**
   * Handle column filter change
   */
  onColumnFilterChange(filter: ColumnFilter): void {
    if (filter.values.length === 0) {
      this.columnFilters.delete(filter.field)
    } else {
      this.columnFilters.set(filter.field, { values: filter.values, inverted: filter.inverted || false })
    }
    this.hasFilters = Boolean(this.filters.searchTerm || this.filters.status || this.columnFilters.size > 0)
    this.applyFiltersAndSort()
    this.cdr.detectChanges()
  }

  /**
   * Handle column header click for sorting
   */
  onColumnHeaderClick(field: string): void {
    if (this.sortField === field) {
      // Toggle direction or clear sort
      if (this.sortDirection === 'desc') {
        this.sortDirection = 'asc'
      } else {
        // Clear sort (back to default)
        this.sortField = null
        this.sortDirection = 'desc'
      }
    } else {
      // New sort field
      this.sortField = field
      this.sortDirection = 'desc'
    }
    this.applyFiltersAndSort()
    this.cdr.detectChanges()
  }

  /**
   * Remove a specific column filter
   */
  removeColumnFilter(field: string): void {
    this.columnFilters.delete(field)
    // Update hasFilters - important for bulk actions
    this.hasFilters = Boolean(this.filters.searchTerm || this.filters.status || this.columnFilters.size > 0)
    this.applyFiltersAndSort()
    this.cdr.detectChanges()
  }

  /**
   * Clear all column filters
   */
  clearAllColumnFilters(): void {
    this.columnFilters.clear()
    this.sortField = null
    this.sortDirection = 'desc'
    // Update hasFilters - important for bulk actions
    this.hasFilters = Boolean(this.filters.searchTerm || this.filters.status || this.columnFilters.size > 0)
    this.applyFiltersAndSort()
    this.cdr.detectChanges()
  }

  /**
   * Get active filters as array for display in search bar
   */
  getActiveFiltersArray(): { field: string; label: string; values: string[]; inverted: boolean }[] {
    const result: { field: string; label: string; values: string[]; inverted: boolean }[] = []
    const dataSource = this.getCurrentRegistrationsDataSource()

    // Map system field names to friendly labels
    const systemFieldLabels: { [key: string]: string } = {
      '#created_at': 'Created at',
      '#email': 'Email Status',
      '#sms': 'SMS Status',
      '#attend': 'Attendance',
      '#approve': 'Approval Status',
      '#approved_by': 'Approved / Declined By',
      '#whatsapp': 'WhatsApp Status'
    }

    // Map filter values to friendly labels
    const valueLabels: { [key: string]: { [value: string]: string } } = {
      '#attend': { yes: 'Attended', no: 'Not Attended' },
      '#approve': { '0': 'Pending', '1': 'Pending Payment', '2': 'Awaiting Revision', '3': 'Updated', '4': 'Approved', '5': 'Declined' },
      '#email': {
        none: 'Not Sent',
        Sent: 'Sent',
        Delivery: 'Delivered',
        Bounce: 'Bounced',
        Complaint: 'Complaint',
        error: 'Error'
      },
      '#sms': { none: 'Not Sent', Pending: 'Pending', Sent: 'Received', Failed: 'Failed', 'Not Supported': 'Not Supported' },
      '#whatsapp': { none: 'Not Sent', read: 'Read', sent: 'Sent', delivered: 'Delivered', failed: 'Failed' }
    }

    // Day attendance value labels (applies to #day1, #day2, etc.)
    const dayValueLabels: { [value: string]: string } = {
      attended: 'Attended',
      missed: 'Missed',
      expected: 'Expected'
    }

    this.columnFilters.forEach((filterConfig, field) => {
      const colDef = dataSource?.columnDefinitions?.find((c) => c.field === field)
      let label = systemFieldLabels[field] || colDef?.header || field

      // Handle day column labels (e.g., #day1 -> "Day 1")
      const dayMatch = field.match(/^#day(\d+)$/)
      if (dayMatch) {
        label = `Day ${dayMatch[1]}`
      }

      // Format values for display
      let displayValues = filterConfig.values.map((v) => {
        // Check if there's a friendly label for this value
        if (valueLabels[field] && valueLabels[field][v]) {
          return valueLabels[field][v]
        }
        // Handle day column values
        if (dayMatch && dayValueLabels[v]) {
          return dayValueLabels[v]
        }
        // Handle date range format (from:to)
        if (v.includes(':') && (field === '#created_at' || field.includes('date'))) {
          const [from, to] = v.split(':')
          if (from && to) return `${from} to ${to}`
          if (from) return `From ${from}`
          if (to) return `Until ${to}`
        }
        // Handle has_content / no_content
        if (v === 'has_content') return 'Has content'
        if (v === 'no_content') return 'No content'
        return v
      })

      result.push({
        field,
        label,
        values: displayValues,
        inverted: filterConfig.inverted
      })
    })

    return result
  }

  /**
   * Get the label for the currently active filter column
   */
  getActiveFilterLabel(): string {
    if (!this.activeFilterColumn) return ''
    // Use stored label if available (for system columns)
    if (this.activeFilterLabel) return this.activeFilterLabel
    const dataSource = this.getCurrentRegistrationsDataSource()
    const colDef = dataSource?.columnDefinitions?.find((c) => c.field === this.activeFilterColumn)
    return colDef?.header || this.activeFilterColumn
  }

  /**
   * Restore filter state from datasource's initializeRequest
   * This ensures UI state matches the actual filter state when switching personas
   */
  private _restoreFilterStateFromDataSource(): void {
    const dataSource = this.getCurrentRegistrationsDataSource()
    if (!dataSource?.initializeRequest) return

    const request = dataSource.initializeRequest

    // Restore column filters from fieldFilters
    if (request.fieldFilters) {
      this.columnFilters.clear()
      Object.entries(request.fieldFilters).forEach(([field, filterConfig]) => {
        // Handle both new format { values: [...], inverted?: boolean } and old format [...]
        const isNewFormat = filterConfig && !Array.isArray(filterConfig) && 'values' in filterConfig
        const values = isNewFormat ? (filterConfig as any).values : filterConfig
        const inverted = isNewFormat ? (filterConfig as any).inverted : false

        if (values && values.length > 0) {
          this.columnFilters.set(field, {
            values: Array.isArray(values) ? values : [values],
            inverted: inverted || false
          })
        }
      })
    }

    // Restore sort state
    if (request.sortField !== undefined) {
      this.sortField = request.sortField
    }
    if (request.sortDirection) {
      this.sortDirection = request.sortDirection
    }

    // Restore search term (update internal state without triggering API call)
    if (request.searchTerm) {
      this._lastSearchTerm = request.searchTerm
      this.filters.searchTerm = true
      // Set search term in data-header without triggering change event
      this.shouldSetSearchTerm = request.searchTerm
      setTimeout(() => {
        this.shouldSetSearchTerm = null
        this.cdr.detectChanges()
      }, 0)
    }

    // Update hasFilters flag
    this.hasFilters = Boolean(this.filters.searchTerm || this.filters.status || this.columnFilters.size > 0)

    // Update datasource's hasFilters
    if (dataSource) {
      dataSource.hasFilters = this.hasFilters
    }

    // Trigger change detection to update UI
    this.cdr.detectChanges()
  }

  /**
   * Apply filters and sort to the data source
   */
  private applyFiltersAndSort(): void {
    const dataSource = this.getCurrentRegistrationsDataSource()
    if (!dataSource) return

    // Convert Map to object for API call
    const fieldFilters: { [key: string]: { values: string[]; inverted?: boolean } } = {}
    this.columnFilters.forEach((filterConfig, field) => {
      fieldFilters[field] = { values: filterConfig.values, inverted: filterConfig.inverted }
    })

    // Update datasource's hasFilters — used for filtered select-all selectionCount
    dataSource.hasFilters = this.hasFilters

    // Trigger a new search with the filters
    dataSource.setFieldFilters(fieldFilters, this.sortField, this.sortDirection)
  }

  // ============================================
  // Saved Filters Methods
  // ============================================

  /**
   * Get saved filters for the current persona
   */
  getCurrentPersonaSavedFilters(): SavedFilter[] {
    const dataSource = this.getCurrentRegistrationsDataSource()
    if (!dataSource?.form?.options?.saved_filters) return []
    return dataSource.form.options.saved_filters || []
  }

  /**
   * Save current filters to persona options
   */
  saveFilter(filterName: string): void {
    const dataSource = this.getCurrentRegistrationsDataSource()
    if (!dataSource?.form) {
      this.toastService.error('Error', 'Unable to save filter - no persona selected')
      return
    }

    // Get current user info
    const currentUser = {
      name: this.authService.userDisplayName || 'Unknown',
      email: this.authService.userEmail || ''
    }

    // Build filter object
    const fieldFiltersObj: { [key: string]: { values: string[]; inverted?: boolean } } = {}
    this.columnFilters.forEach((config, field) => {
      fieldFiltersObj[field] = { values: config.values, inverted: config.inverted }
    })

    const newFilter: SavedFilter = {
      id: this.generateFilterId(),
      name: filterName,
      created_by: currentUser,
      created_at: new Date().toISOString(),
      filters: {
        fieldFilters: fieldFiltersObj,
        searchTerm: this._lastSearchTerm || '',
        sortField: this.sortField,
        sortDirection: this.sortDirection
      }
    }

    // Get existing filters and add new one
    const form = dataSource.form
    const currentOptions = form.options || {}
    const savedFilters = currentOptions.saved_filters || []
    savedFilters.push(newFilter)

    // Update persona options via API
    this.personaService
      .updateForm({
        id: form.id,
        event_id: form.event_id,
        options: { ...currentOptions, saved_filters: savedFilters }
      })
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: () => {
          // Update local form object
          form.options = { ...currentOptions, saved_filters: savedFilters }
          this.toastService.success('Filter saved', `"${filterName}" has been saved`)
          this.cdr.detectChanges()
        },
        error: (err) => {
          console.error('Failed to save filter:', err)
          this.toastService.error('Error', 'Failed to save filter')
        }
      })
  }

  /**
   * Load a saved filter
   */
  loadSavedFilter(filter: SavedFilter): void {
    // Clear existing filters
    this.columnFilters.clear()

    // Apply field filters
    if (filter.filters.fieldFilters) {
      Object.entries(filter.filters.fieldFilters).forEach(([field, config]) => {
        this.columnFilters.set(field, {
          values: config.values,
          inverted: config.inverted || false
        })
      })
    }

    // Apply sort
    this.sortField = filter.filters.sortField || null
    this.sortDirection = filter.filters.sortDirection || 'desc'

    // Apply search term ONLY to the current persona
    if (filter.filters.searchTerm !== undefined && filter.filters.searchTerm !== null) {
      const searchTerm = filter.filters.searchTerm
      this._lastSearchTerm = searchTerm
      this.filters.searchTerm = Boolean(searchTerm)

      const dataSource = this.getCurrentRegistrationsDataSource()
      if (dataSource) {
        // Persist search term on this persona's initializeRequest only
        dataSource.initializeRequest.searchTerm = searchTerm
      }

      // Drive the header search box via the Input binding
      this.shouldSetSearchTerm = searchTerm
      setTimeout(() => {
        this.shouldSetSearchTerm = null
        this.cdr.detectChanges()
      }, 0)
    }

    // Apply filters (field filters + sort + preserved search term) to current persona
    this.applyFiltersAndSort()

    this.toastService.success('Filter loaded', `"${filter.name}" has been applied`)
    this.cdr.detectChanges()
  }

  /**
   * Delete a saved filter
   */
  deleteSavedFilter(filterId: string): void {
    const dataSource = this.getCurrentRegistrationsDataSource()
    if (!dataSource?.form) {
      this.toastService.error('Error', 'Unable to delete filter - no persona selected')
      return
    }

    const form = dataSource.form
    const currentOptions = form.options || {}
    const savedFilters = (currentOptions.saved_filters || []).filter((f: SavedFilter) => f.id !== filterId)

    // Update persona options via API
    this.personaService
      .updateForm({
        id: form.id,
        event_id: form.event_id,
        options: { ...currentOptions, saved_filters: savedFilters }
      })
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: () => {
          // Update local form object
          form.options = { ...currentOptions, saved_filters: savedFilters }
          this.toastService.success('Filter deleted', 'The saved filter has been removed')
          this.cdr.detectChanges()
        },
        error: (err) => {
          console.error('Failed to delete filter:', err)
          this.toastService.error('Error', 'Failed to delete filter')
        }
      })
  }

  /**
   * Generate a unique filter ID
   */
  private generateFilterId(): string {
    return 'filter_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9)
  }

  /**
   * Resolves datasource index from persona/form slug.
   */
  private _resolveSelectedPersonaIndex(personas: Persona[], personaSlug: string): number {
    const normalizedSlug = String(personaSlug ?? '')
      .trim()
      .toLowerCase()
    if (!normalizedSlug) return -1

    const formIndex = personas.findIndex(
      (persona) =>
        String(persona?.slug ?? '')
          .trim()
          .toLowerCase() === normalizedSlug
    )
    return formIndex
  }

  /**
   * Apply AI-generated filters to the current data source
   * @param filters The AI-generated filter configuration
   */
  applyAIFilters(
    filters: { fieldFilters: { [key: string]: { values: string[]; inverted?: boolean } }; searchTerm?: string; sortField?: string | null; sortDirection?: 'asc' | 'desc' } | null
  ): void {
    if (!filters) return

    // Clear existing column filters
    this.columnFilters.clear()

    // Apply the AI-generated field filters
    if (filters.fieldFilters) {
      Object.entries(filters.fieldFilters).forEach(([field, config]) => {
        if (config.values && config.values.length > 0) {
          this.columnFilters.set(field, {
            values: config.values,
            inverted: config.inverted || false
          })
        }
      })
    }

    // Apply sort settings
    this.sortField = filters.sortField || null
    this.sortDirection = filters.sortDirection || 'desc'

    // Update hasFilters flag
    this.hasFilters = Boolean(this.filters.searchTerm || this.filters.status || this.columnFilters.size > 0)

    // Apply the filters to the data source
    this.applyFiltersAndSort()
    this.cdr.detectChanges()
  }

  // ============================================
  // Flag Dropdown and Comment Methods
  // ============================================

  /** Currently active flag dropdown registrant */
  activeFlagDropdown: Registration | null = null
  /** Position for the flag dropdown */
  flagDropdownPosition: { top: number; left: number } = { top: 0, left: 0 }

  /**
   * Open flag dropdown menu
   */
  openFlagDropdown(event: MouseEvent, registrant: Registration): void {
    event.stopPropagation()
    if (this.activeFlagDropdown === registrant) {
      this.activeFlagDropdown = null
      this.cdr.detectChanges()
      return
    }

    const button = event.target as HTMLElement
    const buttonElement = button.closest('button') || button
    const rect = buttonElement.getBoundingClientRect()

    this.flagDropdownPosition = {
      top: rect.bottom + 4,
      left: rect.left
    }

    this.activeFlagDropdown = registrant
    this.cdr.detectChanges()
  }

  /**
   * Close flag dropdown
   */
  closeFlagDropdown(): void {
    this.activeFlagDropdown = null
    this.cdr.detectChanges()
  }

  /**
   * Close flag dropdown when clicking outside
   */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.activeFlagDropdown && !(event.target as HTMLElement).closest('.flag-dropdown') && !(event.target as HTMLElement).closest('.flag-btn')) {
      this.closeFlagDropdown()
    }
  }

  /**
   * Set flag with color
   */
  setFlag(registrant: Registration, color: 'red' | 'orange' | 'green'): void {
    this.closeFlagDropdown()
    registrant.setColoredFlag(color)
  }

  /**
   * Set flag with color and open comment modal
   */
  setFlagAndComment(registrant: Registration, color: 'red' | 'orange' | 'green'): void {
    this.closeFlagDropdown()

    const dialog = this.dialogService.open(CommentModalComponent, {
      context: {
        commentContent: '',
        color: color
      } as any,
      closeOnBackdropClick: true,
      autoFocus: false
    })

    dialog.onClose.pipe(takeUntilDestroyed(this._destroyRef)).subscribe((result) => {
      if (result && result.comment !== undefined) {
        registrant.setColoredFlag(color, result.comment || '')
      }
    })
  }

  /**
   * Remove flag
   */
  removeFlag(registrant: Registration): void {
    this.closeFlagDropdown()
    registrant.removeFlag()
  }

  /**
   * View comments
   */
  viewComments(registrant: Registration): void {
    this.closeFlagDropdown()
    // Show comments in a simple dialog
    const flags = registrant.getFlags().filter((f) => f.comment && f.comment.trim().length > 0)
    if (flags.length === 0) return

    const commentsHtml = flags
      .map((f) => {
        const flagColor = f.flag === 'red' ? '#d32f2f' : f.flag === 'orange' ? '#f57c00' : '#388e3c'
        const bgColor = f.flag === 'red' ? '#fee2e2' : f.flag === 'orange' ? '#fed7aa' : '#dcfce7'
        // Escape HTML in user input but allow the comment HTML to render
        const escapedBy = f.by.replace(/</g, '&lt;').replace(/>/g, '&gt;')
        const timeStr = new Date(f.time).toLocaleString('en-US', {
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit'
        })
        return `
        <div class="tw-mb-3 tw-flex tw-gap-2 tw-items-start">
          <div class="tw-flex-shrink-0 tw-mt-1">
            <div class="tw-w-8 tw-h-8 tw-rounded-full tw-flex tw-items-center tw-justify-center" style="background-color: ${bgColor} !important;">
              <i class="fas fa-flag" style="color: ${flagColor} !important; font-size: 12px !important;"></i>
            </div>
          </div>
          <div class="tw-flex-1 tw-min-w-0">
            <div class="tw-rounded tw-px-3 tw-py-2 tw-mb-1 tw-bg-white tw-border" style="border-color: ${flagColor} !important; border-width: 1px;">
              <div class="tw-text-gray-900 tw-text-sm tw-leading-relaxed comment-content">${f.comment || ''}</div>
            </div>
            <div class="tw-text-xs tw-text-gray-500 tw-ml-1">
              <span class="tw-font-medium">${escapedBy}</span>
              <span class="tw-mx-1">•</span>
              <span>${timeStr}</span>
            </div>
          </div>
        </div>
      `
      })
      .join('')

    const dialogData = {
      headerText: 'Flag Comments',
      cancelButtonText: 'Close',
      confirmButtonText: '',
      confirmButtonClass: '',
      hideIcon: true,
      messageText: commentsHtml,
      allowHtml: true
    }
    const dialogRef = this.dialog.open(DialogComponent, {
      minWidth: '320px',
      maxWidth: '400px',
      width: '90vw',
      panelClass: 'matdialog',
      data: dialogData
    })
  }
}
